import type { Program } from '@compass/client/types/program';
import type { Workflow } from '@compass/client/types/workflow';
import { workflowService } from '@services/api';
import { queryKeys } from '@services/query.config';
import { useStore } from '@store/index';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

interface OrgWorkflowTypeOption {
  value: string;
  label: string;
}

/**
 * Folds one program's workflows into the running maps:
 * - `typeLabels`: workflow_type -> display label
 * - `byType`: workflow_type -> programs containing it
 * - `typesPerProgram`: programId -> set of distinct workflow types
 */
const foldProgramWorkflows = (
  program: Program,
  workflows: Workflow[],
  typeLabels: Map<string, string>,
  byType: Map<string, Program[]>,
  typesPerProgram: Map<string, Set<string>>,
): void => {
  const seenForProgram = new Set<string>();
  for (const wf of workflows) {
    const type = wf.workflow_type;
    if (!type) continue;
    if (!typeLabels.has(type)) typeLabels.set(type, wf.name || type);
    if (!seenForProgram.has(type)) {
      seenForProgram.add(type);
      byType.set(type, [...(byType.get(type) || []), program]);
    }
  }
  if (program.id) typesPerProgram.set(program.id, seenForProgram);
};

/**
 * Fetches workflows for every program in the selected organization (in parallel) and
 * derives org-wide views keyed by `workflow_type`.
 *
 * `workflow_type` is a global string shared across programs (e.g. "medbv", "pbm"), so a
 * single type can map to many programs. Used by the Create/Edit Team dialogs to let an
 * admin pick a workflow type and then associate the team with the programs that contain it.
 *
 * @param enabled - When false, no requests are made (e.g. while a dialog is closed).
 */
export const useOrgProgramWorkflows = (enabled = true) => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const getProgramsArray = useStore(state => state.getProgramsArray);

  const programs = useMemo(() => getProgramsArray(), [getProgramsArray]);

  const workflowQueries = useQueries({
    queries: programs.map(program => ({
      queryKey: queryKeys.workflows.byProgram(program.id || ''),
      queryFn: () =>
        workflowService.getWorkflowsByProgram({
          organization_id: selectedOrganizationId!,
          program_id: program.id!,
        }),
      enabled: enabled && !!selectedOrganizationId && !!program.id,
      staleTime: 10 * 60 * 1000, // 10 minutes - workflows don't change often
    })),
  });

  const { workflowTypeOptions, programsByWorkflowType, programWorkflowTypeCount } = useMemo(() => {
    // type -> display label (workflow name preferred, falling back to the type string)
    const typeLabels = new Map<string, string>();
    // type -> programs that contain a workflow of that type
    const byType = new Map<string, Program[]>();
    // programId -> set of distinct workflow types in that program
    const typesPerProgram = new Map<string, Set<string>>();

    programs.forEach((program, index) => {
      const workflows = workflowQueries[index]?.data?.workflows || [];
      foldProgramWorkflows(program, workflows, typeLabels, byType, typesPerProgram);
    });

    const options: OrgWorkflowTypeOption[] = [...typeLabels.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

    return {
      workflowTypeOptions: options,
      programsByWorkflowType: byType,
      programWorkflowTypeCount: typesPerProgram,
    };
  }, [programs, workflowQueries]);

  const isLoading = enabled && workflowQueries.some(query => query.isLoading);
  const isError = workflowQueries.some(query => query.isError);

  return {
    programs,
    workflowTypeOptions,
    programsByWorkflowType,
    programWorkflowTypeCount,
    isLoading,
    isError,
  };
};
